import { AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import {
  CategoriesService,
  CreateCategoryModel,
  Localization,
  NestedCategories,
  SingleCategoryResponse,
} from '~/shared/api'
import { handleAxiosError, mapDataForSelect, parseQuery } from '~/shared/lib'
import { flattenArrayWithCategories } from '../lib/helpers'
import { StoresService } from '~/shared/api/services/stores'
import { convertLocalizations } from '~/features/stores/controllers/general-field/helper'
import { Store } from '~/shared/api/services/global/types'
import { getNamesFromCategory } from '../lib/get-names-from-category'

const defaultLocalization = {
  name: '',
  description: '',
  content: '',
  seoTitle: '',
  seoKeywords: '',
  seoDescription: '',
}

const defaultCategoryProductAvailabilities = [
  'preorder',
  'drop_ship',
  'hard_stock',
  'special_order',
]

const defaultCategoryLocalizations: Localization[] = [
  {
    ...defaultLocalization,
    languageCode: 'RU',
  },
]

const defaultCategoryLocalizationMap = {
  RU: defaultCategoryLocalizations[0],
}

const initialCreateCategoryData: CreateCategoryModel = {
  category: {
    attachmentId: null,
    enabled: true,
    iconAttachmentId: null,
    productAvailabilities: [],
    showInMenu: true,
    slug: '',
    name: '',
    sortOrder: 50,
    storeId: '',
  },
  localization: {},
}

const initialSingleCategoryResponse: SingleCategoryResponse = {
  enabled: true,
  storeId: '',
  slug: null,
  showInMenu: true,
  sortOrder: 50,
  attachmentId: null,
  iconAttachmentId: null,
  productAvailabilities: defaultCategoryProductAvailabilities,
  localizations: defaultCategoryLocalizations,
  store: {
    address: null,
    created_at: null,
    domain: null,
    id: '',
    logo: null,
    legal_entity_id: null,
    name: null,
    phone: null,
    prefix_code: null,
  },
  name: null,
  id: '',
  description: null,
  content: null,
  link: null,
  breadcrumbs: null,
  categories: [],
  alternateLinks: [],
  images: {
    desktop: null,
    icon: null,
    mobile: null,
    original: null,
  },
  seo: {
    description: null,
    image: null,
    jsonSchema: null,
    keywords: null,
    storeLogo: null,
    title: null,
  },
}

export class CategoriesModel {
  stores: Store[] = []

  categories: NestedCategories[] = []

  createCategoryData: CreateCategoryModel = initialCreateCategoryData

  createCategoryDataValidation = {
    isErrorStoreId: false,
  }

  localeLang = 'RU'

  currentEditableCategory: SingleCategoryResponse =
    initialSingleCategoryResponse

  selectedStores: string[] = []

  filteredCategories: NestedCategories[] = this.categories

  updatedCategories: NestedCategories[] = []

  storesLoading = false

  isCreating = false

  isDeleting = false

  isGettingCategory = false

  isLoadingCategoryLocalizations = false

  isListUpdated = false

  isUpdatingCategories = false

  categoriesLoading = false

  isUpdateSuccess = false

  error404 = false

  constructor() {
    makeAutoObservable(this)
  }

  setLocale = (locale: string) => {
    runInAction(() => {
      this.localeLang = locale
    })
  }

  setUpdatedCategories = (data: NestedCategories[]) => {
    runInAction(() => {
      this.updatedCategories = data
    })
  }

  setIsUpdatedList = (value: boolean) => {
    runInAction(() => {
      this.isListUpdated = value
    })
  }

  setIsErrorStoreId = (value: boolean) => {
    runInAction(() => {
      this.createCategoryDataValidation = {
        ...this.createCategoryDataValidation,
        isErrorStoreId: value,
      }
    })
  }

  saveCategory = async (value: CreateCategoryModel, id?: string) => {
    runInAction(() => {
      this.isCreating = true
      this.error404 = false
    })

    const activeLocalization =
      value.localization[this.localeLang] ||
      Object.values(value.localization)[0]
    const mappedLocal = Object.values(value.localization)
      .map((obj) => ({
        ...obj,
        name: obj.name || null,
        description: obj.description || null,
        content: obj.content || null,
        seoTitle: obj.seoTitle || null,
        seoKeywords: obj.seoKeywords || null,
        seoDescription: obj.seoDescription || null,
      }))
      .filter((item) => Object.keys(item).length)
    const normalizedCategory = {
      ...value.category,
      name: value.category.name || null,
      slug: value.category.slug || null,
      storeId: value.category.storeId,
      seo: {
        ...value.category.seo,
        title: activeLocalization?.seoTitle || null,
        description: activeLocalization?.seoDescription || null,
        keywords: activeLocalization?.seoKeywords || null,
      },
    }

    const payload = {
      category: {
        ...normalizedCategory,
        attachment:
          (value?.category?.attachmentId && [value?.category?.attachmentId]) ||
          null,
      },
      localization: mappedLocal,
    }
    try {
      const data = !id
        ? await CategoriesService.createCategory(payload)
        : await CategoriesService.updateCategory(id, payload)

      runInAction(() => {
        this.createCategoryData = initialCreateCategoryData
      })
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        return error.response?.data
      }
    } finally {
      runInAction(() => {
        this.isCreating = false
        this.currentEditableCategory = initialSingleCategoryResponse
      })
    }
  }

  updateCategoryImage = (
    imageType: 'icon' | 'original',
    value: string | null,
  ) => {
    runInAction(() => {
      const {
        desktop = null,
        icon = null,
        mobile = null,
        original = null,
      } = this.createCategoryData.category.images || {}

      this.createCategoryData = {
        ...this.createCategoryData,
        category: {
          ...this.createCategoryData.category,
          images: {
            desktop,
            icon,
            mobile,
            original,
            [imageType]: value,
          },
        },
      }
    })
  }

  setCategoryImage = (imageType: 'icon' | 'original', url: string) => {
    this.updateCategoryImage(imageType, url)
  }

  setIconAttachmentId = (id: number) => {
    runInAction(() => {
      this.createCategoryData = {
        ...this.createCategoryData,
        category: {
          ...this.createCategoryData.category,
          iconAttachmentId: id,
        },
      }
    })
  }

  setAttachmentId = (id: number) => {
    runInAction(() => {
      this.createCategoryData = {
        ...this.createCategoryData,
        category: {
          ...this.createCategoryData.category,
          attachmentId: id,
        },
      }
    })
  }

  removeCategoryImage = (imageType: 'icon' | 'original') => {
    this.updateCategoryImage(imageType, null)
    const name = imageType === 'icon' ? 'iconAttachmentId' : 'attachmentId'

    this.createCategoryData = {
      ...this.createCategoryData,
      category: { ...this.createCategoryData.category, [name]: null },
    }
  }

  deleteCategory = async (id: string) => {
    runInAction(() => {
      this.isDeleting = true
      this.error404 = false
    })

    try {
      await CategoriesService.deleteCategory(id)
    } catch (error) {
      if (error instanceof AxiosError) {
        return error.response?.data
      }
    } finally {
      runInAction(() => {
        this.isDeleting = false
      })
    }
  }

  getCategories = async (
    queryString: string,
    saveCurrentCategory?: boolean,
  ) => {
    runInAction(() => {
      if (!saveCurrentCategory) {
        this.currentEditableCategory = initialSingleCategoryResponse
      }
      this.categoriesLoading = true
      this.error404 = false
    })

    const params = CategoriesModel.getParamsFromQueryString(queryString)

    try {
      const { data } = await CategoriesService.getCategories({
        params: {
          ...params,
          parentsOnly: 1,
        },
      })
      runInAction(() => {
        this.categories = data
        this.filteredCategories = data
      })
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data)
      }
    } finally {
      runInAction(() => {
        this.categoriesLoading = false
      })
    }
  }

  getCategoriesByStore = async (storeId: string) => {
    this.categoriesLoading = true

    try {
      const { data } = await CategoriesService.getCategories({
        params: {
          storeId,
        },
      })

      runInAction(() => {
        this.categories = data
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.categoriesLoading = false
      })
    }
  }

  getCategoryById = async (id: string) => {
    runInAction(() => {
      this.isGettingCategory = true
      this.error404 = false
    })

    try {
      const { data } = await CategoriesService.getCategoryById(id)
      const languages = await StoresService.getLanguages()
      const storeInfo = await StoresService.getStoreById(
        data.store.id as string,
      )
      const normalizedLocalizations =
        data.localizations?.length > 0
          ? data.localizations
          : defaultCategoryLocalizations
      const activeLocal = Object.values(languages.data)
        .filter((e) =>
          storeInfo.data.storeSettings.languages.some(
            (lan) => lan.code === e.code,
          ),
        )
        .map((e) => e.code)

      const mappedLocalization = normalizedLocalizations.reduce(
        (acc, curr) => {
          if (activeLocal.includes(curr.languageCode)) {
            Object.keys(curr).forEach((key) => {
              acc[curr.languageCode] = {
                ...acc[curr.languageCode],
                [key]: curr[key] || '',
              }
            })
          }
          return acc
        },
        {} as Record<string, Localization>,
      )
      const localizationData = Object.keys(mappedLocalization).length
        ? mappedLocalization
        : defaultCategoryLocalizationMap

      runInAction(() => {
        this.currentEditableCategory = {
          ...data,
          localizations: normalizedLocalizations,
        }

        this.createCategoryData = {
          ...this.createCategoryData,
          category: {
            ...this.createCategoryData.category,
            ...data,
            storeId: data.store.id,
          },
          localization: {
            ...localizationData,
          },
        }
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          runInAction(() => {
            this.error404 = true
          })
        }

        throw new Error(error.response?.data)
      }
    } finally {
      runInAction(() => {
        this.isGettingCategory = false
      })
    }
  }

  updateCategories = async (
    categories: NestedCategories[],
    storeId: string,
  ) => {
    const payload = {
      categories,
      storeId,
    }
    runInAction(() => {
      this.isUpdateSuccess = false
      this.isUpdatingCategories = true
      this.error404 = false
    })

    try {
      await CategoriesService.updateCategories(payload)

      runInAction(() => {
        this.updatedCategories = []
        this.isListUpdated = false
        this.isUpdateSuccess = true
      })
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data)
      }
    } finally {
      runInAction(() => {
        this.isUpdatingCategories = false
        this.currentEditableCategory = initialSingleCategoryResponse
      })
    }
  }

  filterCategoriesByName = (name: string) => {
    const copy = [...this.categories]

    const allNamesArrays: NestedCategories[] = []

    copy.forEach((category) => {
      const names = getNamesFromCategory(category)
      const hasMatch = names.some((el) =>
        el.toLowerCase().includes(name.toLowerCase()),
      )

      if (hasMatch) {
        allNamesArrays.push(category)
      }
    })

    runInAction(() => {
      this.filteredCategories = allNamesArrays
    })
  }

  setCreateCategoryData = async (
    obj: Record<string, unknown>,
    key: keyof CreateCategoryModel,
  ) => {
    runInAction(() => {
      if (obj) {
        this.createCategoryData = {
          ...this.createCategoryData,
          [key]: {
            ...this.createCategoryData[key],
            ...obj,
          },
        }
      }
    })
    if (obj.storeId as string) {
      runInAction(() => {
        this.isLoadingCategoryLocalizations = true
      })

      try {
        const { data } = await StoresService.getStoreById(obj.storeId as string)
        const languages = await StoresService.getLanguages()
        this.createCategoryData = {
          ...this.createCategoryData,
          localization: convertLocalizations<Localization>(
            Object.values(languages.data)
              .filter((e) =>
                data.storeSettings.languages.some(
                  (lang) => lang.code === e.code,
                ),
              )
              .map((e) => ({ ...defaultLocalization, languageCode: e.code })),
          ),
        }
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          throw new Error(error.response?.data)
        }
      } finally {
        runInAction(() => {
          this.isLoadingCategoryLocalizations = false
        })
      }
    }
  }

  get getMappedData() {
    return {
      stores: mapDataForSelect(this.stores, 'id', 'name'),
      filteredCategories: mapDataForSelect(
        this.filteredCategories,
        'id',
        'name',
      ),
      categories: mapDataForSelect(
        flattenArrayWithCategories(this.categories),
        'id',
        'name',
      ),
    }
  }

  static getParamsFromQueryString(value?: string) {
    if (value?.length === 0) return {}

    const { q, ...res } = parseQuery(value || '')

    const productName = q && q.join('').length > 0 ? { q: q.join('') } : {}

    return {
      ...res,
      ...productName,
    }
  }

  setDefaultCreateCategoryData = () => {
    runInAction(() => {
      this.createCategoryData = initialCreateCategoryData
      this.isLoadingCategoryLocalizations = false
      this.createCategoryDataValidation = {
        isErrorStoreId: false,
      }
    })
  }
}

export const sortOptions = {
  swap: true,
  animation: 200,
  delayOnTouchStart: true,
  delay: 2,
  fallbackOnBody: true,
  swapThreshold: 0.65,
}
